import { useApplicantAPI } from "@certn/api";
import {
  ButtonGroup,
  Checkbox,
  GhostButton,
  PrimaryButton,
  SectionMessage,
  Space,
  Text,
  Link,
  LinkButton,
  ModalTrigger,
} from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms";
import { RenderError } from "@certn/forms/src/components/helpers/RenderError";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { hasLabel } from "@certn/forms/src/utils";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import { css } from "@emotion/css";
import type { Layout } from "@jsonforms/core";
import { useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { SkipDsConsent } from "./SkipDSConsent";
import { SkipDSConsentModal } from "./SkipDSConsentModal";
import { useApplication } from "../../ApplicationContext";
import { useHandleChange } from "../../utils";

function getConsentKey(uischema: CascadiaLayoutProps["uischema"]): string {
  const elements = (uischema as Layout)?.elements;
  const scope = (elements?.[0] as { scope?: string })?.scope;
  if (scope) {
    const match = scope.match(/#\/properties\/(\w+)/);
    if (match) return match[1];
  }
  return "ds_consent";
}

function getChildLabel(
  uischema: CascadiaLayoutProps["uischema"],
  childName: string
): string | undefined {
  const consentElement = (uischema as Layout)?.elements?.[0] as
    | Layout
    | undefined;
  const child = consentElement?.elements?.find(
    (el) =>
      "scope" in el &&
      typeof el.scope === "string" &&
      el.scope.endsWith(`/${childName}`)
  );
  return (child as { label?: string } | undefined)?.label;
}

function DsConsentControl(props: CascadiaLayoutProps) {
  const [onSkipConsent, setOnSkipConsent] = useState(false);
  const { isSummary } = useContext(CascadiaJsonFormContext);
  const { navigateToSection, prevSection } = useApplication();
  const { uischema, data } = props;
  const handleChange = useHandleChange();
  const { t } = useTranslation();
  const { caseId } = useParams();
  const { data: caseDetail } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/`
  );

  const consentKey = getConsentKey(uischema);
  const consentData = data?.[consentKey] as Record<string, boolean> | undefined;
  const checkTypes: string[] = caseDetail?.check_types ?? [];
  const certificateLabel = checkTypes.some((ct: string) =>
    ct.includes("DS_LVL2_BARRED")
  )
    ? t("Level 2 with Barred Disclosure Certificate")
    : checkTypes.some((ct: string) => ct.includes("DS_LVL2"))
      ? t("Level 2 Disclosure Certificate")
      : checkTypes.some((ct: string) => ct.includes("BASIC_UK_DS"))
        ? t("Level 1 Disclosure Certificate")
        : undefined;

  if (isSummary) {
    return null;
  }

  if (!hasLabel(uischema)) {
    return <RenderError>Invalid Category</RenderError>;
  }

  const renderCheckbox = (
    id: string,
    isSelected: boolean,
    onChange: (value: boolean) => void,
    children: React.ReactNode
  ) => (
    <Checkbox testID={id} isSelected={isSelected} onChange={onChange}>
      {children}
    </Checkbox>
  );

  const allConsentsChecked =
    !!consentData?.has_read_privacy_policy &&
    !!consentData?.has_authorized_certn_to_submit_application &&
    !!consentData?.has_consented_to_certn_viewing_certificate;

  const isNextButtonEnabled = onSkipConsent || allConsentsChecked;

  return (
    <BaseLayout {...props} className={styles.layout}>
      <Text as="h1" style="form/page-title">
        {uischema.label ?? <Trans>Disclosure Scotland Consent</Trans>}
      </Text>
      {onSkipConsent && (
        <SkipDsConsent clientName={caseDetail?.customer_name} />
      )}
      {!onSkipConsent && (
        <>
          <Text style="form/body-primary">
            {certificateLabel
              ? t(
                  "To allow us to apply for your {{certificateLabel}} from Disclosure Scotland, please review the following statements and provide your consent by checking the boxes below.",
                  { certificateLabel }
                )
              : uischema.options?.subLabel ?? (
                  <Trans>
                    To allow us to apply for your criminal record check from
                    Disclosure Scotland, please review the following statements
                    and provide your consent by checking the boxes below.
                  </Trans>
                )}
          </Text>
          <div>
            {renderCheckbox(
              `${consentKey}.has_read_privacy_policy`,
              !!consentData?.has_read_privacy_policy,
              (value) =>
                handleChange(`${consentKey}.has_read_privacy_policy`, value),
              <Text style="form/body-secondary">
                {getChildLabel(uischema, "has_read_privacy_policy") ?? (
                  <Trans>
                    I have read the Disclosure Scotland privacy policy for
                    applicants and understand how Disclosure Scotland will
                    process my personal data.
                  </Trans>
                )}
              </Text>
            )}
            <div style={{ paddingLeft: 28, marginTop: -2 }}>
              <Link
                testID="ds-privacy-policy-link"
                linkStyle="monochrome"
                textStyle="global/body-secondary"
                textColor="text-weak"
                href="https://www.mygov.scot/privacy?via=http://www.disclosurescotland.co.uk/about/PrivacyPolicyandDisclaimer.htm"
                target="_blank"
              >
                <Trans>View Disclosure Scotland privacy policy</Trans>
              </Link>
            </div>
          </div>
          {renderCheckbox(
            `${consentKey}.has_authorized_certn_to_submit_application`,
            !!consentData?.has_authorized_certn_to_submit_application,
            (value) =>
              handleChange(
                `${consentKey}.has_authorized_certn_to_submit_application`,
                value
              ),
            <Text style="form/body-secondary">
              {certificateLabel
                ? t(
                    "I consent to Certn submitting an application to Disclosure Scotland on my behalf for a {{certificateLabel}}.",
                    { certificateLabel }
                  )
                : getChildLabel(
                    uischema,
                    "has_authorized_certn_to_submit_application"
                  ) ?? (
                    <Trans>
                      I consent to Certn submitting an application to Disclosure
                      Scotland on my behalf for a Disclosure Certificate.
                    </Trans>
                  )}
            </Text>
          )}
          {renderCheckbox(
            `${consentKey}.has_consented_to_certn_viewing_certificate`,
            !!consentData?.has_consented_to_certn_viewing_certificate,
            (value) =>
              handleChange(
                `${consentKey}.has_consented_to_certn_viewing_certificate`,
                value
              ),
            <Text style="form/body-secondary">
              {certificateLabel
                ? t(
                    "I consent to Certn digitally receiving and viewing my {{certificateLabel}} prior to providing it to me.",
                    { certificateLabel }
                  )
                : getChildLabel(
                    uischema,
                    "has_consented_to_certn_viewing_certificate"
                  ) ?? (
                    <Trans>
                      I consent to Certn digitally receiving and viewing my
                      Disclosure Certificate prior to providing it to me.
                    </Trans>
                  )}
            </Text>
          )}
          <hr
            style={{
              border: "none",
              borderTop: "1px solid var(--border-base)",
              margin: 0,
            }}
          />
          <Text style="form/body-secondary">
            <Trans>
              You may opt out of the processing of your data for this purpose.
            </Trans>{" "}
            <ModalTrigger
              trigger={
                <LinkButton
                  linkStyle="monochrome"
                  textStyle="global/body-secondary-link"
                  textColor="text-weak"
                  testID="skip-ds-consent-link"
                >
                  <Trans>Opt out of this process</Trans>
                </LinkButton>
              }
            >
              <SkipDSConsentModal
                setOnSkipConsent={setOnSkipConsent}
                consentKey={consentKey}
              />
            </ModalTrigger>
          </Text>
          <SectionMessage type="info" icon="info">
            <Text style="form/body-secondary" as="strong">
              <Trans>Need more information about Disclosure Scotland?</Trans>
            </Text>
            <br />
            <Trans>
              Questions or comments about how Disclosure Scotland processes
              personal information can be directed to their Data Protection
              Officer at
            </Trans>{" "}
            <Link
              testID="ds-dpo-email"
              target="_blank"
              textStyle="global/body-secondary"
              textColor="text-weak"
              href="mailto:DSDPO@gov.scot"
            >
              DSDPO@gov.scot
            </Link>
          </SectionMessage>
        </>
      )}
      <div className={styles.actionBar}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              testID="section-back-button"
              leftIcon="chevron_left"
              onPress={async () => navigateToSection(prevSection)}
            >
              <Trans>Back</Trans>
            </GhostButton>
          )}
          <Space />
          {props.data && (
            <PrimaryButton
              testID="section-next-step-button"
              type="submit"
              isDisabled={!isNextButtonEnabled}
            >
              <Trans>Next step</Trans>
            </PrimaryButton>
          )}
        </ButtonGroup>
      </div>
    </BaseLayout>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  actionBar: css({
    paddingTop: "var(--space-6)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-6)",
    borderTop: "1px solid var(--border-base)",
  }),
};

DsConsentControl.displayName = "DSConsent";

export const DsConsentControlRenderer =
  withCascadiaLayoutProps(DsConsentControl);
